/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useCallback, useEffect, useState } from 'react';

/* -------------------------------------------------------------------------- */
/*                                  Hook                                      */
/* -------------------------------------------------------------------------- */
export function useLocalStorage<T>(
	key: string,
	initialValue: T
): [T, (value: T | ((val: T) => T)) => void] {
	const readValue = useCallback((): T => {
		if (typeof window === 'undefined')
			return initialValue;

		try {
			const item = window.localStorage.getItem(key);
			if (!item) return initialValue;

			try {
				return JSON.parse(item);
			} catch {
				return item as unknown as T;
			}
		} catch (error) {
			console.warn(
				`Error reading localStorage key “${key}”:`,
				error
			);
			return initialValue;
		}
	}, [initialValue, key]);

	const [storedValue, setStoredValue] =
		useState<T>(readValue);

	const setValue = useCallback(
		(value: T | ((val: T) => T)) => {
			try {
				const valueToStore =
					value instanceof Function
						? value(storedValue)
						: value;

				setStoredValue(valueToStore);

				window.localStorage.setItem(
					key,
					JSON.stringify(valueToStore)
				);

				window.dispatchEvent(
					new Event('local-storage-update')
				);
			} catch (error) {
				console.error(
					`Error setting localStorage key “${key}”:`,
					error
				);
			}
		},
		[key, storedValue]
	);

	useEffect(() => {
		const handleStorageChange = () => {
			setStoredValue(readValue());
		};

		// Événement personnalisé pour la même page
		window.addEventListener(
			'local-storage-update',
			handleStorageChange
		);
		// Événement natif pour les autres onglets
		window.addEventListener(
			'storage',
			handleStorageChange
		);

		return () => {
			window.removeEventListener(
				'local-storage-update',
				handleStorageChange
			);
			window.removeEventListener(
				'storage',
				handleStorageChange
			);
		};
	}, [readValue]);

	return [storedValue, setValue];
}
